import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert } from '../atoms/Alert/Alert';
import { Button } from '../atoms/Button/Button';
import { useTranslation } from '../../i18n/useTranslation';
import { useOptionalCapture } from './CaptureContext';

/** The banner hides itself after this long; the counts on the Capture tab and "To review" stay. */
export const NOTE_READY_AUTO_HIDE_MS = 20_000;

/**
 * "Note ready — Review" (wireframe "Note Ready Banner"): shown on whatever
 * page the user is on when an uploaded note finished processing. Polite
 * announcement; the failure variant is a warning with "Open".
 */
export function NoteReadyBanner() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const capture = useOptionalCapture();
  const ready = capture?.ready ?? null;
  const dismiss = capture?.dismissReady;

  useEffect(() => {
    if (!ready || !dismiss) return undefined;
    const id = setTimeout(dismiss, NOTE_READY_AUTO_HIDE_MS);
    return () => clearTimeout(id);
  }, [ready, dismiss]);

  if (!ready || !dismiss) return null;

  const open = (): void => {
    dismiss();
    navigate('/review');
  };
  const time = new Date(ready.recordedAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  if (ready.failed) {
    return (
      <Alert
        tone="warning"
        live="polite"
        compact
        title={t('voice.ready.failed')}
        action={<Button variant="secondary" size="sm" onClick={open}>{t('voice.ready.open')}</Button>}
        onDismiss={dismiss}
        dismissLabel={String(t('voice.ready.dismiss'))}
        data-testid="note-ready-banner"
      />
    );
  }

  return (
    <Alert
      tone="success"
      live="polite"
      compact
      title={ready.count > 1 ? t('voice.ready.titleMany', { count: ready.count }) : t('voice.ready.title')}
      action={<Button variant="secondary" size="sm" onClick={open}>{t('voice.ready.review')}</Button>}
      onDismiss={dismiss}
      dismissLabel={String(t('voice.ready.dismiss'))}
      data-testid="note-ready-banner"
    >
      {t('voice.ready.body', { count: ready.itemCount, time })}
    </Alert>
  );
}
